import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePresentationRemoteHost, type RemoteHostCommandHandler } from './usePresentationRemoteHost';
import '../components/presentationRemote/RemoteReconnectButton.css';
import { useMainPresentationBgm, type RemoteBgmController } from './useMainPresentationBgm';
import { cancelSpeech, speakText, type SpeakResult } from '../utils/remoteSpeech';
import {
  REMOTE_CHOICE_MIN,
  normalizeAnswerKey,
  normalizeChoices,
  normalizeQuestionMode,
  normalizeQuestionType,
  questionSpeechText,
} from '../utils/remoteQuestion';
import type { RemotePresentationKind } from '../types/presentationRemote';
import {
  RemoteBgmGestureOverlay,
  RemoteCaptionOverlay,
  RemoteNoticeOverlay,
  RemoteQuestionOverlay,
  type RemoteNoticeView,
  type RemoteQuestionView,
} from '../components/presentationRemote/RemotePresentationOverlays';
import { remoteBacklogStore } from '../utils/remoteBacklogStore';
import {
  RemoteMessageBacklog,
  loadRemoteBacklog,
  saveRemoteBacklog,
  capRemoteBacklog,
  type RemoteBacklogItem,
} from '../components/presentationRemote/RemoteMessageBacklog';

/**
 * 리모컨 수신 측 공통(메인 프레젠테이션 · 수업 프레젠):
 * Presence 등록, 공지/질문 팝업, 읽어주기, 배경음악 명령 처리. 반환한 overlays를 페이지에 렌더.
 * presentationKind 'lesson'은 BroadcastChannel 전용(Supabase 미사용).
 */
export function useRemoteReceiver(params: {
  remoteEnabled?: boolean;
  presentationKind?: RemotePresentationKind;
  title?: string;
  enabled?: boolean;
  /** 리모컨 배경음악이 재생을 시작할 때(재개 포함) — 페이지 자체 BGM을 멈추는 데 사용 */
  onRemoteBgmStart?: () => void;
  /** 페이지 자체 BGM 플레이어로 음악 명령 처리(수업 프레젠) — 없으면 내장 플레이어 */
  bgmController?: RemoteBgmController;
  /** 수업 프레젠: 슬라이드 원격 제어 */
  slideState?: Parameters<typeof usePresentationRemoteHost>[0]['slideState'];
  onSlideNav?: Parameters<typeof usePresentationRemoteHost>[0]['onSlideNav'];
}) {
  const { remoteEnabled = false, presentationKind = 'main', title = '', enabled = true } = params;
  const onRemoteBgmStartRef = useRef(params.onRemoteBgmStart);
  onRemoteBgmStartRef.current = params.onRemoteBgmStart;
  /* 리모컨: Presence 등록 (세션 없음/실패 시 무동작) */
  const bgm = useMainPresentationBgm();
  const [remoteNotice, setRemoteNotice] = useState<RemoteNoticeView | null>(null);
  const [remoteQuestion, setRemoteQuestion] = useState<RemoteQuestionView | null>(null);
  const [remoteCaption, setRemoteCaption] = useState<{ id: string; text: string; final: boolean } | null>(null);
  const activeBgm: RemoteBgmController = params.bgmController ?? bgm;
  const bgmRemoteRef = useRef<RemoteBgmController>(activeBgm);
  bgmRemoteRef.current = activeBgm;
  /** 자동재생 정책으로 막힌 읽어주기 — 첫 터치에서 재생 */
  const [pendingSpeech, setPendingSpeech] = useState<{ kind: 'notice' | 'question' | 'caption' | 'preview'; text: string } | null>(null);
  const speakingKindRef = useRef<'notice' | 'question' | 'caption' | 'preview' | null>(null);
  const remoteNoticeRef = useRef<RemoteNoticeView | null>(null);
  remoteNoticeRef.current = remoteNotice;
  const remoteQuestionRef = useRef<RemoteQuestionView | null>(null);
  remoteQuestionRef.current = remoteQuestion;
  const remoteCaptionRef = useRef<{ id: string; text: string; final: boolean } | null>(null);
  remoteCaptionRef.current = remoteCaption;

  /* ---- 남은 메시지(미확인 공지/질문) — 탭별 sessionStorage, DB 미사용 ---- */
  const [backlog, setBacklog] = useState<RemoteBacklogItem[]>(() => loadRemoteBacklog(presentationKind));
  useEffect(() => {
    saveRemoteBacklog(presentationKind, backlog);
  }, [presentationKind, backlog]);
  /** 현재 팝업이 줄(카드)에서 열렸는지 — 이 경우 ✕ 는 확인 처리하지 않음 */
  const [openFromBacklog, setOpenFromBacklog] = useState(false);
  /* 처음엔 미확인이 있을 때만 펼침 */
  const [backlogCollapsed, setBacklogCollapsed] = useState(() => !backlog.some((x) => !x.confirmed));
  /** 확인해도 지우지 않고 confirmed 표시(탭 닫을 때까지 보관) */
  const confirmItem = useCallback(
    (id: string) => setBacklog((b) => b.map((x) => (x.id === id && !x.confirmed ? { ...x, confirmed: true } : x))),
    []
  );
  const pushBacklog = useCallback((item: RemoteBacklogItem) => {
    setBacklog((b) => capRemoteBacklog([...b.filter((x) => x.id !== item.id), item]));
  }, []);
  const unconfirmedCount = backlog.filter((x) => !x.confirmed).length;
  const unconfirmedRef = useRef(unconfirmedCount);
  unconfirmedRef.current = unconfirmedCount;
  /** 열린 팝업이 확인한 항목 다시 보기인지 */
  const [openReview, setOpenReview] = useState(false);
  const popupOpen = !!remoteNotice || !!remoteQuestion;
  const prevPopupOpenRef = useRef(popupOpen);
  useEffect(() => {
    /* 팝업이 닫히면(✕·자동 숨김·교체) 남은 메시지 줄을 펼침 */
    if (prevPopupOpenRef.current && !popupOpen) {
      /* 미확인이 남아 있을 때만 자동으로 펼침 (팝업 닫힘 직후 확인 반영 후 판단) */
      window.setTimeout(() => {
        if (unconfirmedRef.current > 0) setBacklogCollapsed(false);
      }, 0);
      setOpenFromBacklog(false);
      setOpenReview(false);
    }
    prevPopupOpenRef.current = popupOpen;
  }, [popupOpen]);

  const speakRemote = useCallback(async (kind: 'notice' | 'question' | 'caption' | 'preview', text: string): Promise<SpeakResult> => {
    speakingKindRef.current = kind;
    bgmRemoteRef.current.setBgmDucked(true);
    const r = await speakText(text, {
      onEnd: () => {
        if (speakingKindRef.current === kind) speakingKindRef.current = null;
        bgmRemoteRef.current.setBgmDucked(false);
      },
    });
    if (r === 'blocked') setPendingSpeech({ kind, text });
    else setPendingSpeech(null);
    return r;
  }, []);
  const speakResultToAck = (r: SpeakResult) =>
    r === 'ok'
      ? { ok: true }
      : { ok: false, error: r === 'blocked' ? ('tts_blocked' as const) : ('tts_unavailable' as const) };

  /* 공지/질문이 닫히면 해당 읽기 중단 */
  useEffect(() => {
    if (!remoteNotice && speakingKindRef.current === 'notice') {
      cancelSpeech();
      speakingKindRef.current = null;
      bgmRemoteRef.current.setBgmDucked(false);
    }
    if (!remoteNotice) setPendingSpeech((p) => (p?.kind === 'notice' ? null : p));
  }, [remoteNotice]);
  useEffect(() => {
    if (!remoteQuestion && speakingKindRef.current === 'question') {
      cancelSpeech();
      speakingKindRef.current = null;
      bgmRemoteRef.current.setBgmDucked(false);
    }
    if (!remoteQuestion) setPendingSpeech((p) => (p?.kind === 'question' ? null : p));
  }, [remoteQuestion]);
  useEffect(() => {
    if (!remoteCaption && speakingKindRef.current === 'caption') {
      cancelSpeech();
      speakingKindRef.current = null;
      bgmRemoteRef.current.setBgmDucked(false);
    }
    if (!remoteCaption) setPendingSpeech((p) => (p?.kind === 'caption' ? null : p));
  }, [remoteCaption]);
  useEffect(() => () => cancelSpeech(), []);

  useEffect(() => {
    if (!remoteNotice?.durationSec) return undefined;
    const left = remoteNotice.shownAt + remoteNotice.durationSec * 1000 - Date.now();
    const id = window.setTimeout(() => setRemoteNotice((cur) => (cur?.id === remoteNotice.id ? null : cur)), Math.max(0, left));
    return () => window.clearTimeout(id);
  }, [remoteNotice]);

  const handleRemoteCommand = useCallback<RemoteHostCommandHandler>(async (msg) => {
    /* 백그라운드 탭(다른 창의 리모컨에서 보낼 때)은 rAF 가 멈춤 → 응답(ack)이 안 가서 리모컨에 '실패'로 보이던 문제: 짧은 타이머로 보장 */
    const afterPaint = () =>
      new Promise<void>((r) => {
        let done = false;
        const fin = () => {
          if (done) return;
          done = true;
          r();
        };
        window.requestAnimationFrame(fin);
        window.setTimeout(fin, 120);
      });
    switch (msg.type) {
      case 'SHOW_NOTICE': {
        const text = String(msg.payload?.text ?? '').trim().slice(0, 500);
        if (!text) return { ok: false, error: 'invalid_payload' };
        const d = msg.payload.durationSec;
        const view: RemoteNoticeView = {
          id: msg.commandId,
          text,
          durationSec: d === 5 || d === 10 || d === 30 ? d : null,
          shownAt: Date.now(),
        };
        pushBacklog({ kind: 'notice', id: view.id, receivedAt: view.shownAt, seen: false, notice: view });
        /* 열려 있던 팝업은 확인 없이 닫힘 → 남은 메시지에 그대로 남음 */
        setRemoteQuestion(null);
        setOpenFromBacklog(false);
        setRemoteNotice(view);
        await afterPaint();
        if (msg.payload.speak) return speakResultToAck(await speakRemote('notice', text));
        return { ok: true };
      }
      case 'CLOSE_NOTICE': {
        const cur = remoteNoticeRef.current;
        if (cur) confirmItem(cur.id);
        setRemoteNotice(null);
        return { ok: true };
      }
      case 'SHOW_QUESTION': {
        const text = String(msg.payload?.text ?? '').trim().slice(0, 300);
        const requestId = String(msg.payload?.requestId ?? '');
        if (!text || !requestId) return { ok: false, error: 'invalid_payload' };
        let questionType = normalizeQuestionType(msg.payload?.questionType);
        const choices = questionType === 'choice' ? normalizeChoices(msg.payload?.choices) : [];
        if (questionType === 'choice' && choices.length < REMOTE_CHOICE_MIN) questionType = 'short';
        const answerKey =
          normalizeQuestionMode(msg.payload?.mode) === 'answer'
            ? normalizeAnswerKey(msg.payload?.answerKey, questionType, choices.length)
            : null;
        const qView: RemoteQuestionView = {
          requestId,
          text,
          questionType,
          choices,
          mode: answerKey ? 'answer' : 'submit',
          answerKey: answerKey ?? undefined,
          multiSelect:
            questionType === 'choice' &&
            (msg.payload?.multiSelect === true || (answerKey?.choiceIndexes?.length ?? 0) > 1),
          revealAnswer: msg.payload?.revealAnswer !== false,
          fromSessionId: msg.fromSessionId,
          submitted: false,
        };
        setRemoteNotice(null);
        pushBacklog({ kind: 'question', id: requestId, receivedAt: Date.now(), seen: false, question: qView });
        setOpenFromBacklog(false);
        setRemoteQuestion(qView);
        await afterPaint();
        if (msg.payload.speak) {
          return speakResultToAck(await speakRemote('question', questionSpeechText(text, questionType, choices)));
        }
        return { ok: true };
      }
      case 'CLOSE_QUESTION': {
        const rid = msg.payload?.requestId || remoteQuestionRef.current?.requestId;
        if (rid) confirmItem(rid);
        setRemoteQuestion((cur) =>
          !cur || !msg.payload?.requestId || cur.requestId === msg.payload.requestId ? null : cur
        );
        return { ok: true };
      }
      case 'SPEAK_CURRENT': {
        const rawKind = msg.payload?.targetKind;
        const kind = rawKind === 'question' ? 'question' : rawKind === 'caption' ? 'caption' : 'notice';
        const rq = remoteQuestionRef.current;
        const text =
          kind === 'caption'
            ? String(msg.payload?.text ?? remoteCaptionRef.current?.text ?? '').trim().slice(0, 400)
            : kind === 'notice'
              ? remoteNoticeRef.current?.text
              : rq
                ? questionSpeechText(rq.text, rq.questionType ?? 'short', rq.choices ?? [])
                : undefined;
        if (!text) return { ok: false, error: 'nothing_to_speak' };
        return speakResultToAck(await speakRemote(kind, text));
      }
      case 'TTS_PREVIEW': {
        const text = String(msg.payload?.text ?? '').trim().slice(0, 100) || '안녕하세요. 읽어주기 미리듣기입니다.';
        return speakResultToAck(await speakRemote('preview', text));
      }
      case 'MUSIC_PLAY':
        return bgmRemoteRef.current.remotePlay(msg.payload);
      case 'MUSIC_PAUSE':
        return bgmRemoteRef.current.remotePause();
      case 'SHOW_CAPTION': {
        const id = String(msg.payload?.id ?? '');
        const text = String(msg.payload?.text ?? '').trim().slice(0, 400);
        if (!id || !text) return { ok: false, error: 'invalid_payload' };
        const final = !!msg.payload?.final;
        setRemoteCaption({ id, text, final });
        /* 최종 자막은 기록(채팅 목록)에 남김 — 알림 대상 아님(확인 처리됨) */
        if (final) pushBacklog({ kind: 'caption', id: `cap:${id}`, receivedAt: Date.now(), seen: true, confirmed: true, caption: { text } });
        return { ok: true };
      }
      case 'CLOSE_CAPTION':
        setRemoteCaption(null);
        return { ok: true };
      case 'MUSIC_STOP':
        bgmRemoteRef.current.remoteStop();
        return { ok: true };
      default:
        return { ok: false, error: 'not_implemented' };
    }
  }, [speakRemote, pushBacklog, confirmItem]);

  const {
    submitAnswer: submitRemoteAnswer,
    remoteStatus,
    reconnect: reconnectRemote,
  } = usePresentationRemoteHost({
    onCommand: handleRemoteCommand,
    remoteEnabled,
    presentationKind,
    title,
    enabled,
    slideState: params.slideState,
    onSlideNav: params.onSlideNav,
  });

  const openBacklogItem = useCallback((item: RemoteBacklogItem) => {
    setBacklog((b) => b.map((x) => (x.id === item.id && !x.seen ? { ...x, seen: true } : x)));
    setOpenFromBacklog(true);
    setOpenReview(!!item.confirmed);
    if (item.kind === 'caption') {
      setRemoteNotice(null);
      setRemoteQuestion(null);
      setRemoteCaption({ id: item.id, text: item.caption.text, final: true });
      return;
    }
    if (item.kind === 'notice') {
      setRemoteQuestion(null);
      /* 줄에서 연 공지는 자동 숨김 없음 */
      setRemoteNotice({ ...item.notice, durationSec: null, shownAt: Date.now() });
    } else {
      setRemoteNotice(null);
      setRemoteQuestion(
        item.confirmed
          ? { ...item.question, submitted: !!item.answered }
          : { ...item.question, submitted: false, submittedAnswer: undefined }
      );
    }
  }, []);
  const collapseBacklog = useCallback(() => setBacklogCollapsed(true), []);
  const expandBacklog = useCallback(() => setBacklogCollapsed(false), []);
  const openItemId = remoteNotice?.id ?? remoteQuestion?.requestId ?? null;
  /* 새 메시지(실시간 팝업) 중에는 줄을 숨기고, 줄에서 연 팝업일 때는 뒤에 유지 */
  const showBacklog = !backlogCollapsed ? !popupOpen || openFromBacklog : unconfirmedCount > 0 && !popupOpen;

  /* 상단 네비 '남은 메시지' 버튼과 공유 */
  useEffect(() => {
    remoteBacklogStore.publish(enabled, enabled ? unconfirmedCount : 0, enabled ? backlog.length : 0, backlogCollapsed);
  }, [enabled, unconfirmedCount, backlog.length, backlogCollapsed]);
  useEffect(() => {
    remoteBacklogStore.setExpandHandler(() => setBacklogCollapsed(false));
    return () => {
      remoteBacklogStore.setExpandHandler(null);
      remoteBacklogStore.publish(false, 0, 0, false);
    };
  }, []);

  const overlays = (
    <>
        {remoteCaption && !remoteNotice && !remoteQuestion ? (
          <RemoteCaptionOverlay text={remoteCaption.text} onClose={() => setRemoteCaption(null)} />
        ) : null}
        {showBacklog ? (
          <RemoteMessageBacklog
            items={backlog}
            collapsed={backlogCollapsed}
            popupOpen={popupOpen}
            onOpen={openBacklogItem}
            onDismiss={confirmItem}
            onConfirmAll={() =>
              setBacklog((b) => b.map((x) => (x.confirmed || x.id === openItemId ? x : { ...x, confirmed: true })))
            }
            onCollapse={collapseBacklog}
            onExpand={expandBacklog}
          />
        ) : null}
        {remoteNotice ? (
          <RemoteNoticeOverlay
            notice={remoteNotice}
            review={openReview}
            backdrop={!(showBacklog && !backlogCollapsed)}
            onBackdropClose={() => setRemoteNotice(null)}
            onClose={() => {
              /* 실시간 팝업의 ✕ = 확인, 줄에서 연 팝업의 ✕ = 확인 없이 줄로 */
              if (!openFromBacklog) confirmItem(remoteNotice.id);
              setRemoteNotice(null);
            }}
            onConfirm={() => {
              confirmItem(remoteNotice.id);
              setRemoteNotice(null);
            }}
          />
        ) : null}
        {remoteQuestion ? (
          <RemoteQuestionOverlay
            question={remoteQuestion}
            fromBacklog={openFromBacklog}
            readOnly={openReview}
            backdrop={!(showBacklog && !backlogCollapsed)}
            onBackdropClose={() => setRemoteQuestion(null)}
            onSubmit={async (content) => {
              const q = remoteQuestion;
              const ok = await submitRemoteAnswer(q.requestId, q.fromSessionId, content);
              if (ok) {
                /* 제출 = 확인 처리(줄에서 제거). 판정 화면은 팝업에 그대로 표시 */
                setBacklog((b) =>
                  b.map((x) =>
                    x.kind === 'question' && x.id === q.requestId
                      ? { ...x, confirmed: true, answered: true, question: { ...x.question, submittedAnswer: content } }
                      : x
                  )
                );
                setRemoteQuestion((cur) =>
                  cur?.requestId === q.requestId ? { ...cur, submitted: true, submittedAnswer: content } : cur
                );
              }
              return ok;
            }}
            onAnswerAgain={() => setRemoteQuestion((cur) => (cur ? { ...cur, submitted: false } : cur))}
            onDismiss={() => {
              if (!openFromBacklog) confirmItem(remoteQuestion.requestId);
              setRemoteQuestion(null);
            }}
          />
        ) : null}
        {remoteEnabled && (remoteStatus === 'disconnected' || remoteStatus === 'error' || remoteStatus === 'limited') ? (
          <button
            type="button"
            className="cp-remote-reconnect-float"
            onClick={reconnectRemote}
            title="리모트 온라인 연결이 끊겼어요"
          >
            리모트 연결 끊김 · 다시 연결
          </button>
        ) : null}
        {activeBgm.bgmBlocked || pendingSpeech ? (
          <RemoteBgmGestureOverlay
            message={
              activeBgm.bgmBlocked && pendingSpeech
                ? '화면을 한 번 터치하면 음악과 읽어주기가 재생돼요'
                : pendingSpeech
                  ? '화면을 한 번 터치하면 읽어주기가 시작돼요'
                  : undefined
            }
            onTap={() => {
              /* 사용자 제스처 안에서 바로 호출해야 정책 해제됨 */
              if (activeBgm.bgmBlocked) void activeBgm.resumeFromGesture();
              const p = pendingSpeech;
              if (p) {
                setPendingSpeech(null);
                void speakRemote(p.kind, p.text);
              }
            }}
          />
        ) : null}
    </>
  );
  const remoteBgmPlaying = bgm.bgmTransport === 'playing';
  useEffect(() => {
    if (remoteBgmPlaying) onRemoteBgmStartRef.current?.();
  }, [remoteBgmPlaying]);
  /** 페이지에서 자체 BGM을 직접 고르면 리모컨 음악은 정지 */
  const stopRemoteBgm = bgm.handleBgmStop;

  return { overlays, bgm, remoteBgmPlaying, stopRemoteBgm };
}
